import { Injectable } from '@angular/core';
import { Observable, Subject, map, catchError, of, shareReplay } from 'rxjs';

import { GET_COUNTRIES } from '@app/query/country.query';
import { ServicesSubscriberEvent } from '@app/models/others.model';
import { Country } from '@app/models/country.model';
import { ApiService } from '@app/core/graphql/services/api.service';
import { AuthService } from '@app/core/auth/services/auth.service';
import { STATIC_COUNTRIES } from '@app/shared/data/country-codes';

@Injectable()
export class CountriesService {
  subscriber: Subject<ServicesSubscriberEvent> =
    new Subject<ServicesSubscriberEvent>();

  /** The country list is immutable, deployment-global reference data, so the first authenticated
   *  fetch is cached for the app lifetime and shared across every instance of this (per-module)
   *  service. This is why re-opening a modal or navigating no longer re-hits get_countries — e.g.
   *  the pass-modal fetches countries in its constructor, and an apartment overview embeds three
   *  pass-modals; without this they'd each fire the query. Dropped on error so the next call retries. */
  private static countries$: Observable<Country[]> | null = null;

  constructor(private API: ApiService, private authService: AuthService) {}
  getCountries(): Observable<Country[]> {
    // get_countries requires authentication. On public pages (e.g. password reset)
    // there is no session, so skip the PAPI round-trip entirely and use the bundled
    // list — no pointless request, no Unauthorized response.
    if (!this.authService.getToken()) {
      return of(STATIC_COUNTRIES);
    }

    if (!CountriesService.countries$) {
      CountriesService.countries$ = this.API.privateQuery<any>(GET_COUNTRIES).pipe(
        map((response: any) => {
          if (response.success && response.data?.length) {
            this.subscriber.next({
              type: `GET_COUNTRIES`,
              data: response.data as Array<Country>,
            });
            return response.data as Array<Country>;
          }
          // Authenticated but the query failed/returned nothing — fall back to the bundled list.
          return STATIC_COUNTRIES;
        }),
        catchError(() => {
          // Don't cache a failed fetch — let the next caller retry the server.
          CountriesService.countries$ = null;
          return of(STATIC_COUNTRIES);
        }),
        shareReplay(1)
      );
    }

    return CountriesService.countries$;
  }
}
